import React, { PropsWithChildren, useMemo, useState } from 'react';
import { Text } from '@mantine/core';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	INTEREST_DEFINITION_FORM_FIELD,
	useIncomeRightsController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/IncomeRights/useIncomeRightsController.ts';

import { useMutation } from '@tanstack/react-query';
import {
	deleteSecurityInterestDefinition,
	updateSecurityInterestDefinition,
} from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	SecurityFeatureBaseProps,
	buildSecurityRankingDeepLink,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import {
	getDayCountConventionTranslation,
	getSecurityPrincipalTranslation,
	makeDayCountConventionDropdownOptions,
	makeSecurityPrincipalDropdownOptions,
} from '@dealstack/frontend/shared/incomeRights.ts';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';

import {
	CreateUpdateSecurityInterestDefinitionDto,
	SecurityFeatureType,
	SecurityInterestDefinitionPrincipal,
	SecurityIssuedIn,
} from '@dealstack/shared/lib/generated';
import { SecurityInterestDefinitionFormDto } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/IncomeRights/incomeRights.zod.ts';
import { FormProvider } from 'react-hook-form';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';
import { DetailEditableField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/DetailEditableField.tsx';
import { NumberInput, Select } from 'react-hook-form-mantine';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { numberToUiPercent } from '@dealstack/shared/lib/format/number.ts';
import {
	getPeriodType,
	makePeriodTimeUnitOptions,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/period-dropdown-options.tsx';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput.tsx';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc.ts';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { convertCurrencyToSymbol } from '@dealstack/shared/lib/format/currency/currency.ts';
import { EditableInfoField } from '@dealstack/frontend/shared/components/EditableInfoField/EditableInfoField.tsx';
import { SecurityRankField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityRankField/SecurityRankField.tsx';
import { useNavigate } from 'react-router-dom';

export const IncomeRightsInterest = ({
	security,
	onCancel,
	refreshDetails,
	hasEditorPermission = true,
	relativeRanksMap,
}: PropsWithChildren<SecurityFeatureBaseProps>) => {
	const tCommonUI = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const [isEdit, setIsEdit] = useState(!security?.securityInterestDefinition);
	const navigate = useNavigate();
	const navigateToRanking = (featureType: SecurityFeatureType) =>
		navigate(buildSecurityRankingDeepLink(security?.id, featureType));

	const { form, resetForm } = useIncomeRightsController(security);

	const deleteInterestDefinitionRequest = useMutation({
		mutationFn: deleteSecurityInterestDefinition,
	});
	const updateInterestDefinitionRequest = useMutation({
		mutationFn: updateSecurityInterestDefinition,
	});

	const isLoading =
		updateInterestDefinitionRequest.isPending || deleteInterestDefinitionRequest.isPending;

	const formHasCompoundPeriodValue = !!form.watch(
		INTEREST_DEFINITION_FORM_FIELD.compoundingPeriod
	);

	const principal = form.watch(INTEREST_DEFINITION_FORM_FIELD.principal);
	const disablePrincipalFields = security?.issuedIn !== SecurityIssuedIn.UNITS;
	const disablePrincipalValue =
		disablePrincipalFields ||
		principal === SecurityInterestDefinitionPrincipal.ISSUE_COST_PER_UNIT;
	const inputDisabledStyles = {
		input: {
			color: 'transparent !important',
		},
	};

	const dayCountConventionOptions = makeDayCountConventionDropdownOptions(tCommonUI);
	const principalDropdownOptions = makeSecurityPrincipalDropdownOptions(tCommonUI);

	const securityRanks = useMemo(() => {
		if (!security) {
			return { interest: null, principal: null };
		}

		const matchedInterestSecRank = security.securityRanks?.find(
			(secRank) => secRank.featureType === SecurityFeatureType.INTEREST
		);
		const matchedPrincipalSecRank = security.securityRanks?.find(
			(secRank) => secRank.featureType === SecurityFeatureType.PRINCIPAL
		);
		const interest = matchedInterestSecRank
			? {
					...matchedInterestSecRank,
					relativeSortOrder: relativeRanksMap?.get(matchedInterestSecRank.rankId)
						?.relativeSortOrder,
				}
			: null;
		const principal = matchedPrincipalSecRank
			? {
					...matchedPrincipalSecRank,
					relativeSortOrder: relativeRanksMap?.get(matchedPrincipalSecRank.rankId)
						?.relativeSortOrder,
				}
			: null;

		return { interest, principal };
	}, [security, relativeRanksMap]);

	const getPrincipalValuePlaceholder = (): string => {
		switch (principal) {
			case SecurityInterestDefinitionPrincipal.ISSUE_COST_PER_UNIT:
				return tCapManage('securities.interest_definition.principal.issue_cost_per_unit');
			case SecurityInterestDefinitionPrincipal.VALUE_PER_UNIT:
				return tCapManage('securities.interest_definition.principal.value_per_unit');
			case SecurityInterestDefinitionPrincipal.VALUE_FOR_TOTAL_ISSUE:
				return tCapManage('securities.interest_definition.principal.value_for_total_issue');
			default:
				return '';
		}
	};

	const handleUpdateInterestDefinition = (args: SecurityInterestDefinitionFormDto) => {
		const securityId = security?.id ?? '';

		const body: CreateUpdateSecurityInterestDefinitionDto = {
			...args,
			principalValue: args?.principalValue ?? null,
		};
		updateInterestDefinitionRequest.mutate(
			{
				securityId,
				body,
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage('toasts.success.income_rights_updated_successfully'),
					});

					refreshDetails?.(securityId);
					setIsEdit(false);
				},
			}
		);
	};

	const handleDeleteInterestDefinition = () => {
		const securityId = security?.id ?? '';
		deleteInterestDefinitionRequest.mutate(securityId, {
			onSuccess: () => {
				showToastV2({
					title: tCapManage('toasts.success.income_rights_deleted_successfully'),
				});
				refreshDetails?.(securityId);
				setIsEdit(false);
			},
		});
	};

	const handleCancel = () => {
		resetForm();
		onCancel?.();
	};

	return (
		<EditableInfoContainer
			title={tCapManage('securities.incomeRights_interest.title')}
			isEdit={isEdit}
			setIsEdit={setIsEdit}
			disableButtons={isLoading}
			onUpdate={form.handleSubmit(handleUpdateInterestDefinition)}
			onRemove={handleDeleteInterestDefinition}
			onCancel={handleCancel}
			hideRemove={!security?.securityInterestDefinition?.id}
			hasEditorPermission={hasEditorPermission}
			updateBtnDataTestId={TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_FEATURE_ROW_UPDATE_BUTTON}
		>
			<FormProvider {...form}>
				{isLoading && <InlineLoader />}

				<form
					onSubmit={form.handleSubmit(handleUpdateInterestDefinition)}
					style={{ width: '100%' }}
				>
					<EditableInfoContainerGrid $gridColumns={4}>
						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.interest_definition.principal.label')}
							formInput={
								<Select
									data-testid={TEST_ELEMENT_IDS.CAP_INCOME_RIGHTS_PRINCIPAL_TYPE}
									name={INTEREST_DEFINITION_FORM_FIELD.principal}
									control={form.control}
									size="md"
									rightSection={<Icon variant="caret-down" size="lg" />}
									rightSectionProps={{
										style: { pointerEvents: 'none' },
									}}
									data={principalDropdownOptions.map((o) => ({
										value: o.value,
										label: o.label ?? '',
									}))}
									disabled={disablePrincipalFields}
									styles={
										disablePrincipalFields ? inputDisabledStyles : undefined
									}
								/>
							}
							data={
								disablePrincipalFields
									? LONG_DASH
									: getSecurityPrincipalTranslation(
											tCommonUI,
											security?.securityInterestDefinition?.principal
										)
							}
						/>

						<DetailEditableField
							editMode={isEdit}
							label={tCapManage(
								'securities.interest_definition.principal_value.label'
							)}
							formInput={
								<NumberInput
									name={INTEREST_DEFINITION_FORM_FIELD.principalValue}
									control={form.control}
									placeholder={getPrincipalValuePlaceholder()}
									decimalScale={2}
									disabled={disablePrincipalValue}
									styles={disablePrincipalValue ? inputDisabledStyles : undefined}
									size="md"
									leftSection={
										<Text size="lg" c="#7b8991">
											{convertCurrencyToSymbol(security?.currency)}
										</Text>
									}
									data-testid={TEST_ELEMENT_IDS.CAP_INCOME_RIGHTS_PRINCIPAL_VALUE}
									hideControls
								/>
							}
							data={
								disablePrincipalValue ||
								typeof security?.securityInterestDefinition?.principalValue !==
									'number'
									? LONG_DASH
									: security?.securityInterestDefinition?.principalValue
							}
						/>

						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.incomeRights.rate.label')}
							formInput={
								<NumberInput
									name={INTEREST_DEFINITION_FORM_FIELD.interestFloatingRate}
									control={form.control}
									size="md"
									decimalScale={3}
									rightSection={<Icon variant="percentage" size="vlg" />}
									className="percentage-input"
									data-testid={TEST_ELEMENT_IDS.CAP_INCOME_RIGHTS_RATE_INPUT}
								/>
							}
							data={numberToUiPercent(
								security?.securityInterestDefinition?.interestFloatingRate,
								true,
								3
							)}
						/>

						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.incomeRights.accrualPeriod.label')}
							formInput={
								<Select
									name={INTEREST_DEFINITION_FORM_FIELD.accrualPeriod}
									control={form.control}
									size="md"
									rightSection={<Icon variant="caret-down" size="lg" />}
									rightSectionProps={{
										style: { pointerEvents: 'none' },
									}}
									data={makePeriodTimeUnitOptions(tCommonUI)}
									data-testid={
										TEST_ELEMENT_IDS.CAP_INCOME_RIGHTS_ACC_PERIOD_SELECT
									}
								/>
							}
							data={getPeriodType(
								tCommonUI,
								security?.securityInterestDefinition?.accrualPeriod
							)}
						/>

						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.incomeRights.dayCountConvention.label')}
							formInput={
								<Select
									name={INTEREST_DEFINITION_FORM_FIELD.dayCountConvention}
									control={form.control}
									size="md"
									rightSection={<Icon variant="caret-down" size="lg" />}
									rightSectionProps={{
										style: { pointerEvents: 'none' },
									}}
									data={dayCountConventionOptions.map((o) => ({
										value: o.value,
										label: o.label ?? '',
									}))}
									data-testid={TEST_ELEMENT_IDS.CAP_INCOME_RIGHTS_DAY_CONVENTION}
								/>
							}
							data={getDayCountConventionTranslation(
								tCommonUI,
								security?.securityInterestDefinition?.dayCountConvention
							)}
						/>

						<EditableInfoField
							editMode={isEdit}
							label={tCapManage(
								'securities.incomeRights.seniorityRankInterest.label'
							)}
							formInput={
								<SecurityRankField
									unranked={!securityRanks.interest}
									rankName={securityRanks.interest?.rank.displayName}
									sortOrder={securityRanks.interest?.relativeSortOrder || 1}
									totalRanks={relativeRanksMap?.size}
									onClick={() => navigateToRanking(SecurityFeatureType.INTEREST)}
								/>
							}
							customValueElement={
								<SecurityRankField
									unranked={!securityRanks.interest}
									rankName={securityRanks.interest?.rank.displayName}
									sortOrder={securityRanks.interest?.relativeSortOrder || 1}
									totalRanks={relativeRanksMap?.size}
									showContainer={false}
								/>
							}
						/>

						<EditableInfoField
							editMode={isEdit}
							label={tCapManage(
								'securities.incomeRights.seniorityRankPrincipal.label'
							)}
							formInput={
								<SecurityRankField
									unranked={!securityRanks.principal}
									rankName={securityRanks.principal?.rank.displayName}
									sortOrder={securityRanks.principal?.relativeSortOrder || 1}
									totalRanks={relativeRanksMap?.size}
									onClick={() => navigateToRanking(SecurityFeatureType.PRINCIPAL)}
								/>
							}
							customValueElement={
								<SecurityRankField
									unranked={!securityRanks.principal}
									rankName={securityRanks.principal?.rank.displayName}
									sortOrder={securityRanks.principal?.relativeSortOrder || 1}
									totalRanks={relativeRanksMap?.size}
									showContainer={false}
								/>
							}
						/>

						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.incomeRights.deemedStartDate.label')}
							formInput={
								<MantineDateInput
									name={INTEREST_DEFINITION_FORM_FIELD.deemedStartDate}
									size="md"
									clearable
									onChange={(value) => {
										const isoDate = browserDateToMidnightUTC(value) ?? '';

										form.setValue(
											INTEREST_DEFINITION_FORM_FIELD.deemedStartDate,
											isoDate
										);
									}}
									data-testid={
										TEST_ELEMENT_IDS.CAP_INCOME_RIGHTS_DEEMED_START_DATE_INPUT
									}
								/>
							}
							data={formatStringDateToUI(
								security?.securityInterestDefinition?.deemedStartDate,
								DATE_FORMATS.MONTH_DAY_YEAR
							)}
						/>

						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.incomeRights.compoundingPeriod.label')}
							formInput={
								<Select
									name={INTEREST_DEFINITION_FORM_FIELD.compoundingPeriod}
									control={form.control}
									size="md"
									rightSection={
										formHasCompoundPeriodValue ? undefined : (
											<Icon variant="caret-down" size="lg" />
										)
									}
									rightSectionProps={
										formHasCompoundPeriodValue
											? undefined
											: {
													style: { pointerEvents: 'none' },
												}
									}
									data={makePeriodTimeUnitOptions(tCommonUI)}
									allowDeselect
									clearable
									placeholder={tCommonUI(
										'common_placeholders.select_period_unit'
									)}
									data-testid={
										TEST_ELEMENT_IDS.CAP_INCOME_RIGHTS_COMPOUNDING_PERIOD_SELECT
									}
								/>
							}
							data={getPeriodType(
								tCommonUI,
								security?.securityInterestDefinition?.compoundingPeriod
							)}
						/>

						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.incomeRights.firstInterestDate.label')}
							formInput={
								<MantineDateInput
									name={INTEREST_DEFINITION_FORM_FIELD.firstInterestDate}
									size="md"
									clearable
									onChange={(value) => {
										const isoDate = browserDateToMidnightUTC(value) ?? '';

										form.setValue(
											INTEREST_DEFINITION_FORM_FIELD.firstInterestDate,
											isoDate
										);
									}}
									data-testid={
										TEST_ELEMENT_IDS.CAP_INCOME_RIGHTS_FIRST_INTEREST_DATE_INPUT
									}
								/>
							}
							data={formatStringDateToUI(
								security?.securityInterestDefinition?.firstInterestDate,
								DATE_FORMATS.MONTH_DAY_YEAR
							)}
						/>
					</EditableInfoContainerGrid>
				</form>
			</FormProvider>
		</EditableInfoContainer>
	);
};
